import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@et/environment';
import {
  ElsiPackageReminderState,
  ElsiPackageReminderStateCreateDto,
  ElsiReminderPolicy,
  ElsiReminderPolicyBase,
  ElsiReminderPolicyCreateDto,
} from '@et/typings';
import { catchError, map, Observable, of, retry } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ElsiRemindersService {
  private baseUrl = environment.profilev2.domain;
  private readonly http = inject(HttpClient);

  /**
   * Gets the ELSI reminders policy for a company.
   * @param companyId - The ID of the company to get the policy for.
   * @returns An observable of the ELSI reminders policy.
   */
  getCompanyPolicy(companyId: string): Observable<ElsiReminderPolicy | null> {
    return this.http
      .get<ElsiReminderPolicyBase<ElsiReminderPolicy>>(
        `${this.baseUrl}/api/Reminder/ReminderAccount/ByCompanyId/${companyId}`,
      )
      .pipe(
        catchError((err) => {
          if (err.status === 404) {
            return of({ data: null });
          }
          throw err;
        }),
        retry({ count: 2, delay: 200 }),
        map((res) => res.data),
      );
  }

  /**
   * Creates a new ELSI reminders policy for a company.
   * @param policy - The policy to create.
   * @returns An observable of the created ELSI reminders policy.
   */
  createCompanyPolicy(
    policy: ElsiReminderPolicyCreateDto,
  ): Observable<ElsiReminderPolicy> {
    return this.http
      .post<ElsiReminderPolicyBase<ElsiReminderPolicy>>(
        `${this.baseUrl}/api/Reminder/ReminderAccount`,
        policy,
      )
      .pipe(
        retry({ count: 2, delay: 200 }),
        map((res) => res.data),
      );
  }

  updateCompanyPolicy(
    policy: ElsiReminderPolicy,
  ): Observable<ElsiReminderPolicy> {
    return this.http
      .put<ElsiReminderPolicyBase<ElsiReminderPolicy>>(
        `${this.baseUrl}/api/Reminder/ReminderAccount`,
        policy,
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Deletes an ELSI reminders policy for a company.
   * @param accountId - The ID of the account to delete the policy for.
   * @returns An observable of the deleted ELSI reminders policy.
   */
  deleteCompanyPolicy(accountId: string): Observable<string> {
    const params = new HttpParams().set('accountId', accountId);
    return this.http
      .delete<ElsiReminderPolicyBase<string>>(
        `${this.baseUrl}/api/Reminder/ReminderAccount`,
        { params },
      )
      .pipe(
        retry({ count: 2, delay: 200 }),
        map((res) => res.data),
      );
  }

  /**
   * Creates a new ELSI reminders policy for a package.
   * @param policyState - The policy state to create.
   * @returns An observable of the created ELSI reminders policy.
   */
  createPackageReminderState(
    policyState: ElsiPackageReminderStateCreateDto,
  ): Observable<ElsiPackageReminderState> {
    return this.http
      .post<ElsiReminderPolicyBase<ElsiPackageReminderState>>(
        `${this.baseUrl}/api/Reminder/ReminderPackage`,
        policyState,
      )
      .pipe(
        retry({ count: 2, delay: 200 }),
        map((res) => res.data),
      );
  }
}
